import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefreshEffect } from "@/context/RefreshContext";
import PageHeader from "@/components/PageHeader";
import HaftantrittStatistik from "@/components/HaftantrittStatistik";
import { LayoutDashboard, UserX, ArrowUpRight, Crosshair, Link2, CheckCircle2, XCircle, ShieldCheck, TrendingUp, Trophy, DoorOpen } from "lucide-react";

const TOP_CARDS = [
  { key: "inhaftierungen", label: "Aktive Inhaftierungen", icon: UserX, accent: "text-amber-400", border: "hover:border-amber-500/50", to: "/inhaftierung" },
  { key: "chaingangs", label: "Chain-Gangs", icon: Link2, accent: "text-zinc-300", border: "hover:border-zinc-600", to: "/chaingangs" },
];

const HAFTANTRITT_CARDS = [
  { key: "haftantritt", label: "Alle Haftantritte", icon: DoorOpen, accent: "text-primary", border: "hover:border-primary/50", to: "/inhaftierung" },
  { key: "haftantritt_erschienen", label: "Erschienen", icon: CheckCircle2, accent: "text-emerald-400", border: "" },
  { key: "haftantritt_nicht_erschienen", label: "Nicht Erschienen", icon: XCircle, accent: "text-red-400", border: "" },
];

const PAROLE_CARDS = [
  { key: "parole", label: "Aktive Parole", icon: Crosshair, accent: "text-red-400", border: "hover:border-red-500/50", to: "/parole" },
  { key: "parole_erfolgreich", label: "Erfolgreiche Parole", icon: CheckCircle2, accent: "text-emerald-400", border: "" },
  { key: "parole_fehlgeschlagen", label: "Fehlgeschlagene Parole", icon: XCircle, accent: "text-red-400", border: "" },
];

function StatCard({ c, i, value }) {
  const Icon = c.icon;
  const content = (
    <>
      <div className="flex items-start justify-between">
        <Icon className={`h-5 w-5 ${c.accent}`} />
        {c.to && <ArrowUpRight className="h-4 w-4 text-zinc-700 group-hover:text-zinc-400 transition-colors duration-150" />}
      </div>
      <div className="font-mono text-4xl font-semibold text-zinc-100 mt-4">{value ?? "0"}</div>
      <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500 mt-1">{c.label}</div>
    </>
  );
  const baseClass = `group relative bg-[#0C0E12] border border-zinc-800 rounded-sm p-5 transition-colors duration-150 ${c.border} sadcr-fade`;
  if (!c.to) {
    return (
      <div data-testid={`stat-${c.key}`} className={baseClass} style={{ animationDelay: `${i * 40}ms` }}>
        {content}
      </div>
    );
  }
  return (
    <Link to={c.to} data-testid={`stat-${c.key}`} className={baseClass} style={{ animationDelay: `${i * 40}ms` }}>
      {content}
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [statistik, setStatistik] = useState({ officer_inhaftierungen: [], top_haeftlinge: [] });

  const loadAll = useCallback(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/statistik").then((r) => setStatistik(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useRefreshEffect(loadAll);

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Statistik"
        title="Statistik"
        description={`Willkommen zurück, ${user?.name || "Officer"}.`}
        icon={LayoutDashboard}
      />
      <div className="p-8 space-y-10">
        {/* Status */}
        <section className="space-y-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Status</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TOP_CARDS.map((c, i) => (
              <StatCard key={c.key} c={c} i={i} value={stats[c.key]} />
            ))}
          </div>
        </section>

        {/* Haftantritt */}
        <section className="space-y-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Haftantritt</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HAFTANTRITT_CARDS.map((c, i) => (
              <StatCard key={c.key} c={c} i={i} value={stats[c.key]} />
            ))}
          </div>
        </section>

        {/* Haftantritt-Statistik pro Kalenderwoche */}
        <section>
          <HaftantrittStatistik />
        </section>

        {/* Parole */}
        <section className="space-y-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Parole</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAROLE_CARDS.map((c, i) => (
              <StatCard key={c.key} c={c} i={i} value={stats[c.key]} />
            ))}
          </div>
        </section>

        {/* Statistik */}
        <section className="space-y-4">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Statistik</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RankPanel
              title="Officer Inhaftierungen"
              icon={ShieldCheck}
              accent="text-primary"
              testid="stat-officer-inhaftierungen"
              items={statistik.officer_inhaftierungen}
              labelKey="dienstnummer"
              emptyLabel="Keine CO-Dienstnummern erfasst"
            />
            <RankPanel
              title="Top-Häftlinge"
              icon={Trophy}
              accent="text-amber-400"
              testid="stat-top-haeftlinge"
              items={statistik.top_haeftlinge}
              labelKey="name"
              emptyLabel="Keine Häftlinge erfasst"
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function RankPanel({ title, icon: Icon, accent, items, labelKey, emptyLabel, testid }) {
  const list = (items || []).slice(0, 10);
  const max = list.length ? list[0].count : 0;
  return (
    <div data-testid={testid} className="bg-[#0C0E12] border border-zinc-800 rounded-sm sadcr-fade">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-zinc-800">
        <Icon className={`h-4 w-4 ${accent}`} />
        <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-zinc-200">{title}</h3>
        <TrendingUp className="h-3.5 w-3.5 text-zinc-700 ml-auto" />
      </div>
      {list.length === 0 ? (
        <div className="px-5 py-10 text-center font-mono text-sm text-zinc-600">{emptyLabel}</div>
      ) : (
        <div className="divide-y divide-zinc-900">
          {list.map((it, idx) => (
            <div key={it[labelKey]} className="flex items-center gap-3 px-5 py-3">
              <span className={`font-mono text-xs w-6 shrink-0 ${idx < 3 ? accent : "text-zinc-600"}`}>
                #{idx + 1}
              </span>
              <span className="text-sm text-zinc-200 truncate flex-1">{it[labelKey]}</span>
              <div className="hidden sm:block flex-1 max-w-[120px] h-1.5 bg-zinc-900 rounded-full overflow-hidden">
                <div className="h-full bg-primary/70" style={{ width: `${max ? (it.count / max) * 100 : 0}%` }} />
              </div>
              <span className="font-mono text-sm font-semibold text-zinc-100 w-8 text-right shrink-0">{it.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
